import api from "../api/axios";

export const ESSENTIAL_VISITOR_STORAGE_KEY = "vg_cookie_consent_visitor_id";
export const ESSENTIAL_VISITOR_COOKIE_NAME = "vg_essential_visitor_id";
const ESSENTIAL_VISITOR_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

const EVENTS_ENDPOINT = "/events/analytics/collect/";
const CLUBS_ENDPOINT = "/clubs/analytics/collect/";
const FLUSH_INTERVAL_MS = 1200;
const MAX_BATCH_SIZE = 40;

const queues = {
  events: [],
  clubs: [],
};

const timers = {
  events: null,
  clubs: null,
};

const inFlight = {
  events: false,
  clubs: false,
};

const pendingImpressionKeys = {
  events: new Set(),
  clubs: new Set(),
};

const mapImpressionKeys = new Set();

function createLocalId(prefix) {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getCookieValue(name) {
  if (typeof document === "undefined") return "";
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function setCookieValue(name, value, maxAgeSeconds = ESSENTIAL_VISITOR_MAX_AGE_SECONDS) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function getLocalStorageValue(key) {
  try {
    return String(localStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function setLocalStorageValue(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function getOrCreateEssentialVisitorId() {
  const localValue = getLocalStorageValue(ESSENTIAL_VISITOR_STORAGE_KEY);
  const cookieValue = String(getCookieValue(ESSENTIAL_VISITOR_COOKIE_NAME) || "").trim();

  const stable = localValue || cookieValue || createLocalId("visitor");

  if (stable && localValue !== stable) {
    setLocalStorageValue(ESSENTIAL_VISITOR_STORAGE_KEY, stable);
  }
  if (stable && cookieValue !== stable) {
    setCookieValue(ESSENTIAL_VISITOR_COOKIE_NAME, stable);
  }

  return stable;
}

function scheduleFlush(type) {
  if (timers[type]) return;
  timers[type] = window.setTimeout(() => {
    timers[type] = null;
    flushQueue(type);
  }, FLUSH_INTERVAL_MS);
}

function impressionQueueKey(type, row) {
  if (row?.action !== "impression") return "";
  const entityId = type === "events" ? row?.event_id : row?.club_id;
  return [
    Number(entityId || 0),
    String(row?.surface || ""),
    row?.map_zoom ?? "",
  ].join(":");
}

async function flushQueue(type) {
  if (inFlight[type]) return;
  const queue = queues[type];
  if (!queue.length) return;

  inFlight[type] = true;
  try {
    while (queues[type].length) {
      const visitorId = getOrCreateEssentialVisitorId();
      const batch = queues[type].splice(0, MAX_BATCH_SIZE);
      batch.forEach((row) => {
        const key = impressionQueueKey(type, row);
        if (key) pendingImpressionKeys[type].delete(key);
      });

      const payload =
        type === "events"
          ? { visitor_id: visitorId, events: batch }
          : { visitor_id: visitorId, clubs: batch };

      const url = type === "events" ? EVENTS_ENDPOINT : CLUBS_ENDPOINT;

      try {
        await api.post(url, payload, {
          meta: {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
            suppressGlobalError: true,
          },
        });
      } catch {
        // Do not retry aggressively to avoid duplicate overcount.
      }
    }
  } finally {
    inFlight[type] = false;
    if (queues[type].length) scheduleFlush(type);
  }
}

function enqueue(type, row) {
  const impressionKey = impressionQueueKey(type, row);
  if (impressionKey && pendingImpressionKeys[type].has(impressionKey)) return;
  if (impressionKey) pendingImpressionKeys[type].add(impressionKey);

  queues[type].push(row);
  if (queues[type].length >= MAX_BATCH_SIZE) {
    flushQueue(type);
    return;
  }
  scheduleFlush(type);
}

export function trackEventEssentialAnalytics({ eventId, action, surface = "", mapZoom = null }) {
  const id = Number(eventId || 0);
  if (!Number.isFinite(id) || id <= 0) return;
  if (action !== "impression" && action !== "open") return;

  const row = {
    event_id: id,
    action,
    surface: String(surface || "").slice(0, 32),
  };

  if (mapZoom !== null && mapZoom !== undefined) {
    const zoom = Number(mapZoom);
    if (Number.isFinite(zoom)) {
      row.map_zoom = zoom;
    }
  }

  enqueue("events", row);
  if (action === "open") {
    void flushQueue("events");
  }
}

export function trackClubEssentialAnalytics({ clubId, action, surface = "" }) {
  const id = Number(clubId || 0);
  if (!Number.isFinite(id) || id <= 0) return;
  if (action !== "impression" && action !== "open") return;

  enqueue("clubs", {
    club_id: id,
    action,
    surface: String(surface || "").slice(0, 32),
  });
  if (action === "open") {
    void flushQueue("clubs");
  }
}

export function trackMapEventImpressionOncePerSession(eventId, zoom = null) {
  const id = Number(eventId || 0);
  if (!Number.isFinite(id) || id <= 0) return;

  const key = `map:${id}`;
  if (mapImpressionKeys.has(key)) return;
  mapImpressionKeys.add(key);

  trackEventEssentialAnalytics({
    eventId: id,
    action: "impression",
    surface: "map",
    mapZoom: zoom,
  });
}
